// src/pages/ProductDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Heart, ShoppingBag, Star, ChevronLeft, Truck, Shield, RefreshCw, Minus, Plus, Share2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import ProductCard from '../components/ProductCard';
import Loader, { SkeletonCard } from '../components/Loader';
import toast from 'react-hot-toast';

export default function ProductDetailPage() {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const { addToCart, toggleWishlist, isInWishlist, setCartOpen } = useApp();

  const [product,     setProduct]     = useState(null);
  const [related,     setRelated]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [quantity,    setQuantity]    = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const [activeTab,   setActiveTab]   = useState('description');

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, 'products', id));
        if (!snap.exists()) { setProduct(null); setLoading(false); return; }
        const data = { id: snap.id, ...snap.data() };
        setProduct(data);

        // Fetch related products (same category)
        const allSnap = await getDocs(collection(db, 'products'));
        const all     = allSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((p) => p.id !== id && p.category === data.category && p.isActive !== false);
        setRelated(all.slice(0, 4));
      } catch (err) {
        console.error(err);
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (loading) return (
    <div className="min-h-screen bg-cream pt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="aspect-square bg-rose-50 rounded-3xl animate-pulse" />
          <div className="space-y-4 pt-4">
            <div className="h-8 bg-rose-50 rounded-full w-3/4 animate-pulse" />
            <div className="h-4 bg-rose-50 rounded-full w-1/2 animate-pulse" />
            <div className="h-10 bg-rose-50 rounded-full w-1/3 animate-pulse" />
            <div className="h-24 bg-rose-50 rounded-2xl animate-pulse" />
          </div>
        </div>
      </div>
    </div>
  );

  if (!product) return (
    <div className="min-h-screen flex items-center justify-center bg-cream pt-20">
      <div className="text-center">
        <div className="text-6xl mb-4">😕</div>
        <h2 className="font-display font-bold text-2xl text-gray-800 mb-4">Product not found</h2>
        <Link to="/products" className="bg-rose-500 text-white px-6 py-3 rounded-2xl hover:bg-rose-600 transition-colors">
          Back to Shop
        </Link>
      </div>
    </div>
  );

  const discount   = product.originalPrice > product.price
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;
  const inWishlist = isInWishlist(product.id);
  const images     = product.images?.length ? product.images : (product.image ? [product.image] : []);

  const handleAddToCart = () => { addToCart(product, quantity); setCartOpen(true); };
  const handleBuyNow    = () => { addToCart(product, quantity); navigate('/checkout'); };
  const handleShare     = async () => {
    try { await navigator.share({ title: product.name, url: window.location.href }); }
    catch { navigator.clipboard.writeText(window.location.href); toast.success('Link copied!'); }
  };

  return (
    <div className="min-h-screen bg-cream pt-20">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center gap-2 text-sm font-body text-gray-400">
          <Link to="/" className="hover:text-rose-500 transition-colors">Home</Link>
          <span>/</span>
          <Link to="/products" className="hover:text-rose-500 transition-colors">Shop</Link>
          <span>/</span>
          <span className="text-gray-700 truncate max-w-[200px]">{product.name}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid lg:grid-cols-2 gap-12">

          {/* Images */}
          <div>
            <div className="relative rounded-3xl overflow-hidden aspect-square bg-white shadow-card mb-4">
              <img
                src={images[activeImage] || 'https://via.placeholder.com/600'}
                alt={product.name}
                className="w-full h-full object-cover"
              />
              {product.badge && (
                <span className="absolute top-4 left-4 bg-white text-rose-600 text-xs font-bold px-3 py-1.5 rounded-full shadow-sm">
                  {product.badge}
                </span>
              )}
              {discount > 0 && (
                <span className="absolute top-4 right-4 bg-rose-500 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                  {discount}% OFF
                </span>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-3">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setActiveImage(i)}
                    className={`w-20 h-20 rounded-2xl overflow-hidden border-2 transition-all ${
                      activeImage === i ? 'border-rose-500 shadow-rose' : 'border-transparent'
                    }`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <button onClick={() => navigate(-1)}
              className="flex items-center gap-1 text-gray-400 hover:text-rose-500 text-sm mb-4 transition-colors font-body">
              <ChevronLeft size={16} /> Back
            </button>

            <h1 className="font-display font-bold text-3xl md:text-4xl text-gray-900 mb-3">{product.name}</h1>

            {/* Rating */}
            {product.rating > 0 && (
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16}
                      className={i < Math.floor(product.rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200'} />
                  ))}
                </div>
                <span className="font-body font-medium text-gray-800 text-sm">{product.rating}</span>
                <span className="text-gray-400 text-sm font-body">({product.reviews || 0} reviews)</span>
              </div>
            )}

            {/* Price */}
            <div className="flex items-center gap-3 mb-6">
              <span className="font-display font-bold text-4xl text-gray-900">
                ₹{Number(product.price).toLocaleString('en-IN')}
              </span>
              {product.originalPrice > product.price && (
                <>
                  <span className="text-gray-400 text-xl line-through font-body">
                    ₹{Number(product.originalPrice).toLocaleString('en-IN')}
                  </span>
                  <span className="bg-green-100 text-green-700 text-sm font-bold px-2.5 py-1 rounded-full">
                    Save ₹{(Number(product.originalPrice) - Number(product.price)).toLocaleString('en-IN')}
                  </span>
                </>
              )}
            </div>

            {/* Tags */}
            {product.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {product.tags.map((tag) => (
                  <span key={tag} className="bg-rose-50 text-rose-500 text-xs px-3 py-1 rounded-full font-body">
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Quantity */}
            <div className="flex items-center gap-4 mb-6">
              <span className="font-body font-medium text-gray-700 text-sm">Quantity:</span>
              <div className="flex items-center gap-3 bg-white border border-rose-200 rounded-2xl px-3 py-2">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="text-gray-500 hover:text-rose-500 transition-colors">
                  <Minus size={16} />
                </button>
                <span className="font-body font-bold text-gray-900 w-6 text-center">{quantity}</span>
                <button onClick={() => setQuantity(Math.min(product.stock || 99, quantity + 1))}
                  className="text-gray-500 hover:text-rose-500 transition-colors">
                  <Plus size={16} />
                </button>
              </div>
              <span className="text-xs text-gray-400 font-body">{product.stock || 0} in stock</span>
            </div>

            {/* CTAs */}
            <div className="flex gap-3 mb-6">
              <button onClick={handleAddToCart}
                className="flex-1 flex items-center justify-center gap-2 bg-rose-500 hover:bg-rose-600 text-white py-4 rounded-2xl font-medium text-lg transition-all shadow-rose hover:shadow-rose-lg">
                <ShoppingBag size={20} /> Add to Cart
              </button>
              <button onClick={() => toggleWishlist(product)}
                className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all border ${
                  inWishlist
                    ? 'bg-rose-500 text-white border-rose-500 shadow-rose'
                    : 'bg-white text-gray-500 border-rose-200 hover:border-rose-400 hover:text-rose-500'
                }`}>
                <Heart size={20} className={inWishlist ? 'fill-white' : ''} />
              </button>
              <button onClick={handleShare}
                className="w-14 h-14 rounded-2xl bg-white text-gray-500 border border-rose-200 hover:border-rose-400 hover:text-rose-500 flex items-center justify-center transition-all">
                <Share2 size={20} />
              </button>
            </div>

            <button onClick={handleBuyNow}
              className="w-full bg-gray-900 hover:bg-gray-800 text-white py-4 rounded-2xl font-medium text-lg transition-all mb-8">
              Buy Now — ₹{(Number(product.price) * quantity).toLocaleString('en-IN')}
            </button>

            {/* Trust */}
            <div className="grid grid-cols-3 gap-3 border-t border-rose-100 pt-6">
              {[
                { Icon: Truck,     text: 'Free Delivery', sub: 'On every order' },
                { Icon: Shield,    text: 'Secure Payment', sub: 'Via Razorpay'  },
                { Icon: RefreshCw, text: 'Easy Returns',   sub: '7-day policy'  },
              ].map(({ Icon, text, sub }) => (
                <div key={text} className="flex flex-col items-center text-center gap-1">
                  <Icon size={20} className="text-rose-400" />
                  <span className="text-xs font-body font-medium text-gray-700">{text}</span>
                  <span className="text-xs text-gray-400 font-body">{sub}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-16">
          <div className="flex gap-1 bg-white rounded-2xl p-1 w-fit mb-6 shadow-soft">
            {['description', 'details', 'reviews'].map((tab) => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`px-6 py-2.5 rounded-xl text-sm font-body font-medium transition-all capitalize ${
                  activeTab === tab ? 'bg-rose-500 text-white shadow-sm' : 'text-gray-500 hover:text-rose-500'
                }`}>
                {tab}
              </button>
            ))}
          </div>
          <div className="bg-white rounded-3xl p-6 shadow-soft">
            {activeTab === 'description' && (
              <div>
                <p className="text-gray-600 font-body leading-relaxed text-base">{product.description}</p>
                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                  {['Handcrafted with love', 'Premium quality materials', 'Gift-ready packaging', 'Personalization available'].map((feat) => (
                    <div key={feat} className="flex items-center gap-2 text-sm font-body text-gray-600">
                      <span className="text-rose-500">✓</span> {feat}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {activeTab === 'details' && (
              <div className="space-y-3">
                {[
                  { label: 'Category', value: product.category },
                  { label: 'Stock',    value: `${product.stock || 0} available` },
                  { label: 'Rating',   value: product.rating ? `${product.rating} / 5` : 'No ratings yet' },
                  { label: 'Reviews',  value: product.reviews || 0 },
                  { label: 'SKU',      value: product.id.slice(0, 8).toUpperCase() },
                ].map(({ label, value }) => (
                  <div key={label} className="flex justify-between py-2 border-b border-rose-50">
                    <span className="text-gray-400 font-body text-sm">{label}</span>
                    <span className="font-body font-medium text-gray-800 text-sm capitalize">{value}</span>
                  </div>
                ))}
              </div>
            )}
            {activeTab === 'reviews' && (
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="text-center">
                    <div className="font-display font-bold text-5xl text-gray-900">{product.rating || '—'}</div>
                    <div className="flex justify-center gap-0.5 mt-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={14}
                          className={i < Math.floor(product.rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-gray-200'} />
                      ))}
                    </div>
                    <div className="text-gray-400 text-sm font-body mt-1">{product.reviews || 0} reviews</div>
                  </div>
                </div>
                <p className="text-gray-400 font-body text-sm italic">Reviews from verified purchasers will appear here.</p>
              </div>
            )}
          </div>
        </div>

        {/* Related Products */}
        {related.length > 0 && (
          <div className="mt-16">
            <h2 className="font-display font-bold text-3xl text-gray-900 mb-8">You May Also Love</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {related.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}